import { requiredFields, requiredFieldsForCreditReissue } from "./requiredFields";
import { runSilentValidation } from "./silent-validation";
import { validateContactInformation } from "./required-field-helper/contact-info-required-helper";
import { validateAssetsInformation } from "./required-field-helper/asset-section-required-helper";
import { validateDeclarationInformation } from "./required-field-helper/declaration-section-required-helper";
import { validateMilitaryInformation } from "./required-field-helper/military-info-required-helper";
import { validateDemographicProvided } from "./required-field-helper/demographic-provided-info-required-helper";
import { validateDemographicInformation } from "./required-field-helper/demographic-info-required-helper";

const noopSetError = () => {};

const reorderResidencesArray = (arr = []) =>
	arr.map((obj, index) => ({ ...obj, id: `Residence/${index}` }));

const buildLoanApplicationData = (applicationData) => {
	const pairArr = [];

	applicationData?.applications?.forEach((item) => {
		pairArr.push(item.borrower);
		pairArr.push(item.coBorrower || item.coborrower || {});
	});

	return pairArr;
};

const buildFlatBorrowerPairData = (applicationData) => buildLoanApplicationData(applicationData);

const buildBorrowerResidences = (applicationData) => {
	const residenceArr = [];

	applicationData?.applications?.forEach((application) => {
		residenceArr.push(...reorderResidencesArray(application?.residences));
	});

	return residenceArr;
};

const getPropertyDetailsFromApplication = (applicationData) => {
	const taxAmount = applicationData?.fees?.find(
		(fee) => fee.feeType === "PropertyTaxes"
	);

	return {
		homeownersInsurance: applicationData?.proposedHazardInsuranceAmount,
		propertyTaxes: taxAmount ? taxAmount.monthlyPayment : 0,
	};
};

const buildDemographicArrays = (applicationData) => {
	const pairArr = [];
	const declarationArr = [];
	const militaryArr = [];
	const demographicProvidedArr = [];
	const demographicInfoArr = [];

	applicationData?.applications?.forEach((application) => {
		const borrower = application?.borrower || {};
		const coborrower = application?.coBorrower || application?.coborrower || {};

		pairArr.push(borrower, coborrower);
		declarationArr.push({ ...borrower }, { ...coborrower });
		militaryArr.push({ ...borrower }, { ...coborrower });
		demographicProvidedArr.push(
			{
				applicationTakenMethodType: borrower.applicationTakenMethodType,
				isEthnicityBasedOnVisual: borrower.isEthnicityBasedOnVisual,
				isRaceBasedOnVisual: borrower.isRaceBasedOnVisual,
				isSexBasedOnVisual: borrower.isSexBasedOnVisual,
			},
			{
				applicationTakenMethodType: coborrower.applicationTakenMethodType,
				isEthnicityBasedOnVisual: coborrower.isEthnicityBasedOnVisual,
				isRaceBasedOnVisual: coborrower.isRaceBasedOnVisual,
				isSexBasedOnVisual: coborrower.isSexBasedOnVisual,
			}
		);
		demographicInfoArr.push({ ...borrower }, { ...coborrower });
	});

	return {
		pairArr,
		declarationArr,
		militaryArr,
		demographicProvidedArr,
		demographicInfoArr,
	};
};

const validateBorrowerSection = (applicationData, options = {}) => {
	if (!applicationData?.applications?.length) {
		return false;
	}

	const loanApplicationData = buildLoanApplicationData(applicationData);
	const propertyInfo = applicationData?.property || {};
	const grossRent =
		applicationData?.applications[0]?.propertyUsageType === "Investor"
			? applicationData?.subjectPropertyGrossRentalIncomeAmount
			: 0;
	const { homeownersInsurance, propertyTaxes } =
		getPropertyDetailsFromApplication(applicationData);

	return runSilentValidation([
		(setError) =>
			requiredFields(
				loanApplicationData,
				propertyInfo,
				grossRent,
				homeownersInsurance,
				propertyTaxes,
				options.loanOriginatorEmail || "",
				options.loanProcessorEmail || "",
				false,
				{},
				setError
			),
	]);
};

const validateAddressSection = (applicationData) => {
	const borrowerPairData = buildFlatBorrowerPairData(applicationData);
	const borrowerResidences = buildBorrowerResidences(applicationData);

	if (!borrowerPairData.length || !borrowerResidences.length) {
		return false;
	}

	return !validateContactInformation(borrowerPairData, borrowerResidences)
};

const validateCreditReissueSection = (applicationData) => {
	const loanApplicationData = buildLoanApplicationData(applicationData);
	const borrowerPairData = buildFlatBorrowerPairData(applicationData);
	const borrowerResidences = buildBorrowerResidences(applicationData);

	if (!loanApplicationData.length) {
		return false;
	}

	if (!requiredFieldsForCreditReissue(loanApplicationData, noopSetError)) {
		return false;
	}

	return !validateContactInformation(borrowerPairData, borrowerResidences);
};

export const validateAssetsSection = (applicationData) => {
	const applications = applicationData?.applications || [];

	if (!applications.length) {
		return false;
	}

	const borrowerResponseData = applications.map((application) => ({
		borrower: application.borrower,
		coborrower: application.coBorrower || application.coborrower,
	}));
	const borrowerLiability = applications.map(
		(application) => application.liabilities || []
	);
	const borrowerRealEstate = applications.map(
		(application) => application.reoProperties || []
	);

	return !validateAssetsInformation(
		borrowerResponseData,
		borrowerLiability,
		borrowerRealEstate
	);
};

const validateDeclarationsSection = (applicationData) => {
	const {
		pairArr,
		declarationArr,
		militaryArr,
		demographicProvidedArr,
		demographicInfoArr,
	} = buildDemographicArrays(applicationData);

	if (!pairArr.length) {
		return false;
	}

	return !(
		validateDeclarationInformation(pairArr, declarationArr) ||
		validateMilitaryInformation(pairArr, militaryArr) ||
		validateDemographicProvided(pairArr, demographicProvidedArr) ||
		validateDemographicInformation(pairArr, demographicInfoArr)
	);
};



/**
 * Returns nav indices (matching loanSteps) for XML sections with valid mandatory fields.
 * Assets (navIndex 4) is intentionally excluded from auto-completion: users must visit
 * Assets & Liabilities at least once (same as Employment and other non-prevalidated steps).
 * validateAssetsSection is kept for section-level validation when the user opens that step.
 */
export const preValidateXmlSectionsFromData = (applicationData, options = {}) => {
	if (!applicationData || !applicationData?.applications?.length) {
		return [];
	}

	const validators = [
		{ navIndex: 0, validate: () => validateBorrowerSection(applicationData, options) },
		{ navIndex: 1, validate: () => validateAddressSection(applicationData) },
		{ navIndex: 2, validate: () => validateCreditReissueSection(applicationData) },
		{ navIndex: 5, validate: () => validateDeclarationsSection(applicationData) },
	];

	return validators
		.filter(({ validate }) => validate())
		.map(({ navIndex }) => navIndex);
};

export const navIndexToXmlStage = (navIndex) => (navIndex === 0 ? 0 : navIndex + 1);
